import { RefreshCw, Zap } from "lucide-react";
import { Panel } from "@/components/panel";
import { LoadingState } from "@/components/loading-state";
import { QueryErrorState } from "@/components/query-error-state";
import { useScanOpportunities, useRunQuickScan } from "@/sdk/hooks/use-scanner";
import type { ContrarianOpportunity } from "@/sdk/types/market";

function OpportunityRow({ rec }: { rec: ContrarianOpportunity }) {
  const conf = Math.round(rec.confidence * 100);
  const isBuy = rec.signal === "BUY" || rec.signal === "STRONG_BUY";
  const isSell = rec.signal === "SELL" || rec.signal === "STRONG_SELL";
  const referencePrice = rec.avg_price ?? rec.current_price;
  return (
    <div className="flex items-center justify-between py-2.5 px-3 rounded-lg border border-white/5 hover:bg-white/5 transition-colors">
      <div className="min-w-0 flex-1">
        <p className="text-xs text-white/80 truncate font-mono">{rec.market_id}</p>
        <p className="text-xs text-white/40 mt-0.5 truncate">Avg ${referencePrice.toFixed(2)}</p>
      </div>
      <div className="flex items-center gap-3 ml-3 shrink-0">
        <div className="text-right">
          <p className={`text-sm font-bold ${isBuy ? "text-green-400" : isSell ? "text-red-400" : "text-white/40"}`}>
            {rec.signal}
          </p>
          <p className="text-xs text-white/40">${rec.total_volume.toFixed(2)}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-white/40">{rec.current_price.toFixed(2)}</p>
          <p className="text-xs text-white/40">{conf}% conf</p>
        </div>
      </div>
    </div>
  );
}

export function ScanOpportunities() {
  const { data, isLoading, isError, refetch } = useScanOpportunities(20);
  const quickScan = useRunQuickScan();

  const opportunities = data?.opportunities ?? [];

  return (
    <Panel
      title={`Opportunities (${data?.total ?? 0})`}
      actions={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => quickScan.mutate({})}
            disabled={quickScan.isPending}
            className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-blue-600/30 hover:bg-blue-600/50 text-blue-300 disabled:opacity-50 transition-colors"
          >
            <Zap className="h-3 w-3" />
            {quickScan.isPending ? "Scanning…" : "Quick Scan"}
          </button>
          <button type="button" onClick={() => refetch()} className="p-1 rounded hover:bg-white/10 text-white/40">
            <RefreshCw className="h-3 w-3" />
          </button>
        </div>
      }
    >
      {quickScan.isError ? (
        <p role="alert" className="mb-2 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
          Quick scan failed:{" "}
          {quickScan.error instanceof Error ? quickScan.error.message : "unknown error"}
        </p>
      ) : quickScan.data ? (
        <p className="mb-2 rounded border border-white/10 bg-white/5 px-3 py-2 text-xs text-white/60">
          Quick scan: {quickScan.data.summary?.opportunities_found ?? 0} opportunities across{" "}
          {quickScan.data.scan_params?.markets_analyzed ?? 0} markets analyzed
        </p>
      ) : null}
      {isLoading ? (
        <LoadingState text="Loading opportunities…" />
      ) : isError ? (
        <QueryErrorState what="scan opportunities feed" onRetry={() => void refetch()} />
      ) : opportunities.length === 0 ? (
        <p className="text-sm text-white/40 py-6 text-center">No opportunities found — run a scan</p>
      ) : (
        <div className="space-y-1.5">
          {opportunities.map((rec, i) => (
            <OpportunityRow key={i} rec={rec} />
          ))}
        </div>
      )}
    </Panel>
  );
}
